import { useState, MouseEvent } from 'react';
import {
    Typography,
    Stack,
    Chip,
    Box,
    Divider,
    Paper,
    Popover,
    Tooltip
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { processBerlinQuestionnaire, BerlinScoreResult } from '@/ScoreProcessors/BerlinProcessor';
import { processEpworthQuestionnaire, EpworthScoreResult } from '@/ScoreProcessors/EpworthProcessor';
import { processIgiQuestionnaire, IgiScoreResult, IgiSeverity } from '@/ScoreProcessors/IgiProcessor';
import { processLawtonQuestionnaire, LawtonScoreResult, LawtonDependence } from '@/ScoreProcessors/LawtonProcessor';
import { processMeqSaQuestionnaire, MeqSaScoreResult, ChronoType } from '@/ScoreProcessors/MeqSaProcessor';
import { processMoriskyQuestionnaire, MoriskyScoreResult, MoriskyAdherenceLevel } from '@/ScoreProcessors/MoriskyProcessor';
import { processPittsburghQuestionnaire, PittsburghScoreResult } from '@/ScoreProcessors/PittsburghProcessor';
import { processMocabQuestionnaire, MocabScoreResult } from '@/ScoreProcessors/MocabProcessor';
import { isQuestionnaireOfType } from '@/ScoreProcessors/types';

interface QuestionnaireScoreDisplayProps {
    data: any;
    questionnaire: any;
}

export function QuestionnaireScoreDisplay({ data, questionnaire }: QuestionnaireScoreDisplayProps) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const handlePopoverOpen = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = (event?: MouseEvent<HTMLElement>) => {
        if (event) {
            event.stopPropagation();
        }
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const isBerlin = isQuestionnaireOfType(questionnaire, 'berlin');
    const isEpworth = isQuestionnaireOfType(questionnaire, 'epworth');
    const isIgi = isQuestionnaireOfType(questionnaire, 'igi') || isQuestionnaireOfType(questionnaire, 'insomnia');
    const isLawton = isQuestionnaireOfType(questionnaire, 'lawton') || isQuestionnaireOfType(questionnaire, 'aivd');
    const isMeqSa = isQuestionnaireOfType(questionnaire, 'meq-sa');
    const isMorisky = isQuestionnaireOfType(questionnaire, 'morisky') || isQuestionnaireOfType(questionnaire, 'cpap') || isQuestionnaireOfType(questionnaire, 'tsqm-cpap');
    const isPittsburgh = isQuestionnaireOfType(questionnaire, 'pittsburgh');
    const isMocab = isQuestionnaireOfType(questionnaire, 'moca-b') || isQuestionnaireOfType(questionnaire, 'mocab');

    if (!isBerlin && !isEpworth && !isIgi && !isLawton && !isMeqSa && !isMorisky && !isPittsburgh && !isMocab) return null;

    let popoverContent;
    let chipLabel;
    let chipColor: "default" | "error" | "primary" | "secondary" | "info" | "success" | "warning" | undefined;

    if (isBerlin) {
        const score = processBerlinQuestionnaire(data.resource);
        return <BerlinScoreDisplay score={score} onPopoverOpen={handlePopoverOpen} popoverOpen={open} popoverAnchorEl={anchorEl} onPopoverClose={handlePopoverClose} />;
    }

    if (isEpworth) {
        const score = processEpworthQuestionnaire(data.resource);
        return <EpworthScoreDisplay score={score} onPopoverOpen={handlePopoverOpen} popoverOpen={open} popoverAnchorEl={anchorEl} onPopoverClose={handlePopoverClose} />;
    }

    if (isIgi) {
        const score = processIgiQuestionnaire(data.resource);
        return <IgiScoreDisplay score={score} onPopoverOpen={handlePopoverOpen} popoverOpen={open} popoverAnchorEl={anchorEl} onPopoverClose={handlePopoverClose} />;
    }

    if (isLawton) {
        const score = processLawtonQuestionnaire(data.resource);
        return <LawtonScoreDisplay score={score} onPopoverOpen={handlePopoverOpen} popoverOpen={open} popoverAnchorEl={anchorEl} onPopoverClose={handlePopoverClose} />;
    }

    if (isMeqSa) {
        const score = processMeqSaQuestionnaire(data.resource);
        return <MeqSaScoreDisplay score={score} onPopoverOpen={handlePopoverOpen} popoverOpen={open} popoverAnchorEl={anchorEl} onPopoverClose={handlePopoverClose} />;
    }

    if (isMorisky) {
        const score = processMoriskyQuestionnaire(data.resource);
        return <MoriskyScoreDisplay score={score} onPopoverOpen={handlePopoverOpen} popoverOpen={open} popoverAnchorEl={anchorEl} onPopoverClose={handlePopoverClose} />;
    }

    if (isPittsburgh) {
        const score = processPittsburghQuestionnaire(data.resource);
        return <PittsburghScoreDisplay score={score} onPopoverOpen={handlePopoverOpen} popoverOpen={open} popoverAnchorEl={anchorEl} onPopoverClose={handlePopoverClose} />;
    }

    if (isMocab) {
        const score = processMocabQuestionnaire(data.resource);
        return <MocabScoreDisplay score={score} onPopoverOpen={handlePopoverOpen} popoverOpen={open} popoverAnchorEl={anchorEl} onPopoverClose={handlePopoverClose} />;
    }

    return null;
}

interface ScoreDisplayProps<T> {
    score: T;
    onPopoverOpen: (event: MouseEvent<HTMLElement>) => void;
    popoverOpen: boolean;
    popoverAnchorEl: HTMLElement | null;
    onPopoverClose: (event?: MouseEvent<HTMLElement>) => void;
}

function PittsburghScoreDisplay({
    score,
    onPopoverOpen,
    popoverOpen,
    popoverAnchorEl,
    onPopoverClose
}: ScoreDisplayProps<PittsburghScoreResult>) {
    const getQualityColor = (quality: 'good' | 'poor') => {
        return quality === 'good' ? 'success' : 'error';
    };

    const getQualityLabel = (quality: 'good' | 'poor') => {
        return quality === 'good' ? 'Boa Qualidade' : 'Pobre Qualidade';
    };

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Detalhes do Resultado">
                <Chip
                    icon={<InfoOutlined fontSize="small" />}
                    label={`PSQI: ${score.totalScore}/21 - ${getQualityLabel(score.sleepQuality)}`}
                    color={getQualityColor(score.sleepQuality)}
                    size="small"
                    onClick={onPopoverOpen}
                    sx={{
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: 'neutral300.main',
                        backgroundColor: 'neutral100.main',
                        color: 'neutral700.main',
                        "&:hover": {
                            backgroundColor: 'neutral200.main',
                            boxShadow: '1px 1px 16px 1px rgba(0, 0, 0, 0.1)',
                            color: 'neutral700.main',
                            "& .MuiChip-icon": { color: 'neutral700.main' }
                        }
                    }}
                />
            </Tooltip>
            <Popover
                open={popoverOpen}
                anchorEl={popoverAnchorEl}
                onClose={() => onPopoverClose()}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Paper sx={{ p: 2.5, minWidth: 340, maxWidth: 420 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                        Pittsburgh - Índice de Qualidade do Sono (PSQI)
                    </Typography>

                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', mb: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Pontuação total
                                </Typography>
                                <Typography variant="h4" fontWeight={700} color="primary.main">
                                    {score.totalScore}
                                    <Typography component="span" variant="body1" color="text.secondary" fontWeight={400}>/21</Typography>
                                </Typography>
                            </Box>
                            <Chip
                                label={getQualityLabel(score.sleepQuality)}
                                color={getQualityColor(score.sleepQuality)}
                                size="medium"
                                sx={{ fontWeight: 600 }}
                            />
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {score.sleepQuality === 'good'
                                ? 'Pontuação &lt; 5 indica boa qualidade do sono'
                                : 'Pontuação ≥ 5 indica pobre qualidade do sono'}
                        </Typography>
                    </Box>

                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                        Métricas do Sono
                    </Typography>
                    <Stack spacing={1} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2">Duração</Typography>
                            <Chip label={`${score.sleepDuration}h`} size="small" variant="outlined" sx={{ minWidth: 56, fontWeight: 500 }} />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2">Latência</Typography>
                            <Chip label={`${score.sleepLatency}min`} size="small" variant="outlined" sx={{ minWidth: 56, fontWeight: 500 }} />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2">Eficiência</Typography>
                            <Chip label={`${score.sleepEfficiency.toFixed(1)}%`} size="small" variant="outlined" sx={{ minWidth: 56, fontWeight: 500 }} />
                        </Box>
                    </Stack>

                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                        Componentes PSQI
                    </Typography>
                    <Stack spacing={1}>
                        {score.components.map((component, index) => (
                            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                                <Typography variant="body2" sx={{ flex: 1, pr: 2 }}>
                                    {component.name}
                                </Typography>
                                <Chip
                                    label={`${component.score}/${component.maxScore}`}
                                    size="small"
                                    variant="outlined"
                                    color={component.score === 0 ? 'success' : component.score <= 1 ? 'warning' : 'error'}
                                    sx={{ minWidth: 44, fontWeight: 500 }}
                                />
                            </Box>
                        ))}
                    </Stack>

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
                        &lt; 5: Boa qualidade · ≥ 5: Pobre qualidade
                    </Typography>
                </Paper>
            </Popover>
        </Stack>
    );
}

const BERLIN_CATEGORY_LABELS: Record<string, string> = {
    'categoria-01': 'Ronco e sintomas relacionados',
    'categoria-02': 'Sonolência diurna',
    'categoria-03': 'Pressão arterial e IMC'
};

function BerlinScoreDisplay({
    score,
    onPopoverOpen,
    popoverOpen,
    popoverAnchorEl,
    onPopoverClose
}: ScoreDisplayProps<BerlinScoreResult>) {
    const riskLabel = score.highRiskApnea ? 'Alto' : 'Baixo';
    const chipColor = score.highRiskApnea ? 'error' : 'success';

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Detalhes do Resultado">
                <Chip
                    icon={<InfoOutlined fontSize="small" />}
                    label={`Berlin: Risco ${riskLabel} (${score.positiveCategoriesCount}/3 categorias positivas)`}
                    color={chipColor}
                    size="small"
                    onClick={onPopoverOpen}
                    sx={{
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: 'neutral300.main',
                        backgroundColor: 'neutral100.main',
                        color: 'neutral700.main',
                        "&:hover": {
                            backgroundColor: 'neutral200.main',
                            boxShadow: '1px 1px 16px 1px rgba(0, 0, 0, 0.1)',
                            color: 'neutral700.main',
                            "& .MuiChip-icon": { color: 'neutral700.main' }
                        }
                    }}
                />
            </Tooltip>
            <Popover
                open={popoverOpen}
                anchorEl={popoverAnchorEl}
                onClose={() => onPopoverClose()}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Paper sx={{ p: 2.5, minWidth: 340, maxWidth: 420 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                        Questionário de Berlim
                    </Typography>

                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', mb: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Categorias positivas
                                </Typography>
                                <Typography variant="h4" fontWeight={700} color="primary.main">
                                    {score.positiveCategoriesCount}
                                    <Typography component="span" variant="body1" color="text.secondary" fontWeight={400}>/3</Typography>
                                </Typography>
                                {typeof score.bmi === 'number' && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        IMC: {score.bmi}
                                    </Typography>
                                )}
                            </Box>
                            <Chip
                                label={`Risco ${riskLabel}`}
                                color={chipColor}
                                size="medium"
                                sx={{ fontWeight: 600 }}
                            />
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Duas ou mais categorias positivas indicam grande possibilidade de distúrbios do sono.
                        </Typography>
                    </Box>

                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                        Categorias
                    </Typography>
                    <Stack spacing={1}>
                        {score.categories.map((cat) => (
                            <Box key={cat.categoryId} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                                <Typography variant="body2" sx={{ flex: 1, pr: 2 }}>
                                    {BERLIN_CATEGORY_LABELS[cat.categoryId] ?? cat.categoryId.replace('categoria-', 'Categoria ')}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip label={`${cat.totalPoints} pts`} size="small" variant="outlined" sx={{ minWidth: 48, fontWeight: 500 }} />
                                    <Chip
                                        label={cat.isPositive ? 'Positiva' : 'Negativa'}
                                        size="small"
                                        color={cat.isPositive ? 'warning' : 'success'}
                                        sx={{ fontWeight: 500 }}
                                    />
                                </Stack>
                            </Box>
                        ))}
                    </Stack>

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
                        ≥ 2 categorias positivas: risco alto de apneia obstrutiva do sono
                    </Typography>
                </Paper>
            </Popover>
        </Stack>
    );
}

function EpworthScoreDisplay({
    score,
    onPopoverOpen,
    popoverOpen,
    popoverAnchorEl,
    onPopoverClose
}: ScoreDisplayProps<EpworthScoreResult>) {
    const chipLabel = `Sonolência: ${score.excessiveSleepiness ? 'Excessiva' : 'Normal'}`;
    const chipColor = score.excessiveSleepiness ? 'warning' : 'success';

    const popoverContent = (
        <Paper sx={{ p: 2, maxWidth: 400 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Detalhes do Resultado - Epworth
            </Typography>
            <Stack spacing={1}>
                <Typography variant="body2">
                    <strong>Pontuação total:</strong> {score.totalScore}/24
                </Typography>
                <Typography variant="body2">
                    <strong>Interpretação:</strong> {score.excessiveSleepiness ? 'Sonolência excessiva' : 'Sonolência normal'}
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Pontuação por situação:
                </Typography>
                {Object.entries(score.details).map(([linkId, points]) => (
                    <Typography key={linkId} variant="body2">
                        <strong>{linkId}:</strong> {points} pontos
                    </Typography>
                ))}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.8rem' }}>
                    Pontuação ≥ 10 indica sonolência excessiva que deve ser investigada.
                </Typography>
            </Stack>
        </Paper>
    );

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Detalhes do Resultado">
                <Chip
                    icon={<InfoOutlined fontSize="small" />}
                    label={chipLabel}
                    color={chipColor}
                    size="small"
                    onClick={onPopoverOpen}
                    sx={{
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: 'neutral300.main',
                        backgroundColor: 'neutral100.main',
                        color: 'neutral700.main',
                        "&:hover": {
                            backgroundColor: 'neutral200.main',
                            boxShadow: '1px 1px 16px 1px rgba(0, 0, 0, 0.1)',
                            color: 'neutral700.main',
                            "& .MuiChip-icon": {
                                color: 'neutral700.main'
                            }
                        }
                    }}
                />
            </Tooltip>
            <Popover
                open={popoverOpen}
                anchorEl={popoverAnchorEl}
                onClose={() => onPopoverClose()}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {popoverContent}
            </Popover>
        </Stack>
    );
}

const IGI_SEVERITY_LABELS: Record<IgiSeverity, string> = {
    ausente: 'Ausência de insônia clinicamente significativa',
    subclinica: 'Insônia subclínica (limítrofe)',
    moderada: 'Insônia moderada (clinicamente relevante)',
    grave: 'Insônia grave (clinicamente relevante)'
};

const IGI_SEVERITY_SHORT: Record<IgiSeverity, string> = {
    ausente: 'Ausente',
    subclinica: 'Subclínica',
    moderada: 'Moderada',
    grave: 'Grave'
};

const IGI_ITEM_LABELS: Record<string, string> = {
    'difficulty-falling-asleep': 'Dificuldade em pegar no sono',
    'difficulty-staying-asleep': 'Dificuldade em manter o sono',
    'early-waking': 'Despertar muito cedo',
    'sleep-satisfaction': 'Satisfação com o padrão de sono',
    'sleep-interference': 'Interferência nas atividades diurnas',
    'others-perception': 'Percepção dos outros',
    'sleep-anxiety': 'Preocupação com o sono'
};

function IgiScoreDisplay({
    score,
    onPopoverOpen,
    popoverOpen,
    popoverAnchorEl,
    onPopoverClose
}: ScoreDisplayProps<IgiScoreResult>) {
    const getSeverityColor = (severity: IgiSeverity) => {
        if (severity === 'ausente') return 'success';
        if (severity === 'subclinica') return 'info';
        if (severity === 'moderada') return 'warning';
        if (severity === 'grave') return 'error';
        return 'default';
    };

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Detalhes do Resultado">
                <Chip
                    icon={<InfoOutlined fontSize="small" />}
                    label={`IGI: ${score.totalScore}/28 - ${IGI_SEVERITY_SHORT[score.severity]}`}
                    color={getSeverityColor(score.severity)}
                    size="small"
                    onClick={onPopoverOpen}
                    sx={{
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: 'neutral300.main',
                        backgroundColor: 'neutral100.main',
                        color: 'neutral700.main',
                        "&:hover": {
                            backgroundColor: 'neutral200.main',
                            boxShadow: '1px 1px 16px 1px rgba(0, 0, 0, 0.1)',
                            color: 'neutral700.main',
                            "& .MuiChip-icon": { color: 'neutral700.main' }
                        }
                    }}
                />
            </Tooltip>
            <Popover
                open={popoverOpen}
                anchorEl={popoverAnchorEl}
                onClose={() => onPopoverClose()}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Paper sx={{ p: 2.5, minWidth: 340, maxWidth: 420 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                        IGI - Índice de Gravidade da Insônia (ISI)
                    </Typography>

                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', mb: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Pontuação total
                                </Typography>
                                <Typography variant="h4" fontWeight={700} color="primary.main">
                                    {score.totalScore}<Typography component="span" variant="body1" color="text.secondary" fontWeight={400}>/28</Typography>
                                </Typography>
                            </Box>
                            <Chip
                                label={IGI_SEVERITY_SHORT[score.severity]}
                                color={getSeverityColor(score.severity)}
                                size="medium"
                                sx={{ fontWeight: 600 }}
                            />
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {IGI_SEVERITY_LABELS[score.severity]}
                        </Typography>
                    </Box>

                    <Stack spacing={1}>
                        {Object.entries(score.details).map(([linkId, points]) => (
                            <Box key={linkId} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                                <Typography variant="body2" sx={{ flex: 1, pr: 2 }}>
                                    {IGI_ITEM_LABELS[linkId] ?? linkId}
                                </Typography>
                                <Chip label={`${points}/4`} size="small" variant="outlined" sx={{ minWidth: 44, fontWeight: 500 }} />
                            </Box>
                        ))}
                    </Stack>

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
                        0–7: Ausente · 8–14: Subclínica · 15–21: Moderada · 22–28: Grave
                    </Typography>
                </Paper>
            </Popover>
        </Stack>
    );
}

const LAWTON_DEPENDENCE_LABELS: Record<LawtonDependence, string> = {
    independente: 'Independente',
    parcial: 'Dependência parcial',
    total: 'Dependência total'
};

const LAWTON_ITEM_LABELS: Record<string, string> = {
    lawton_1: 'Telefone',
    lawton_2: 'Transporte',
    lawton_3: 'Compras',
    lawton_4: 'Preparar refeições',
    lawton_5: 'Arrumar a casa',
    lawton_6: 'Reparos domésticos',
    lawton_7: 'Lavar e passar roupa',
    lawton_8: 'Tomar medicamentos',
    lawton_9: 'Cuidar das finanças'
};

function LawtonScoreDisplay({
    score,
    onPopoverOpen,
    popoverOpen,
    popoverAnchorEl,
    onPopoverClose
}: ScoreDisplayProps<LawtonScoreResult>) {
    const getDependenceColor = (d: LawtonDependence) => {
        if (d === 'independente') return 'success';
        if (d === 'parcial') return 'warning';
        return 'error';
    };

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Detalhes do Resultado">
                <Chip
                    icon={<InfoOutlined fontSize="small" />}
                    label={`Lawton: ${score.totalScore}/27 - ${LAWTON_DEPENDENCE_LABELS[score.dependence]}`}
                    color={getDependenceColor(score.dependence)}
                    size="small"
                    onClick={onPopoverOpen}
                    sx={{
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: 'neutral300.main',
                        backgroundColor: 'neutral100.main',
                        color: 'neutral700.main',
                        "&:hover": {
                            backgroundColor: 'neutral200.main',
                            boxShadow: '1px 1px 16px 1px rgba(0, 0, 0, 0.1)',
                            color: 'neutral700.main',
                            "& .MuiChip-icon": { color: 'neutral700.main' }
                        }
                    }}
                />
            </Tooltip>
            <Popover
                open={popoverOpen}
                anchorEl={popoverAnchorEl}
                onClose={() => onPopoverClose()}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Paper sx={{ p: 2.5, minWidth: 340, maxWidth: 420 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                        Escala de Lawton e Brody (AIVD)
                    </Typography>

                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', mb: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Pontuação total
                                </Typography>
                                <Typography variant="h4" fontWeight={700} color="primary.main">
                                    {score.totalScore}<Typography component="span" variant="body1" color="text.secondary" fontWeight={400}>/27</Typography>
                                </Typography>
                            </Box>
                            <Chip
                                label={LAWTON_DEPENDENCE_LABELS[score.dependence]}
                                color={getDependenceColor(score.dependence)}
                                size="medium"
                                sx={{ fontWeight: 600 }}
                            />
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {score.dependence === 'independente' && 'Capacidade plena para atividades instrumentais da vida diária.'}
                            {score.dependence === 'parcial' && 'Necessidade de suporte em algumas atividades.'}
                            {score.dependence === 'total' && 'Dependência significativa para atividades instrumentais.'}
                        </Typography>
                    </Box>

                    <Stack spacing={1}>
                        {Object.entries(score.details).map(([linkId, points]) => (
                            <Box key={linkId} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                                <Typography variant="body2" sx={{ flex: 1, pr: 2 }}>
                                    {LAWTON_ITEM_LABELS[linkId] ?? linkId}
                                </Typography>
                                <Chip label={`${points}/3`} size="small" variant="outlined" sx={{ minWidth: 44, fontWeight: 500 }} />
                            </Box>
                        ))}
                    </Stack>

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
                        27: Independente · 18–26: Dependência parcial · ≤18: Dependência total
                    </Typography>
                </Paper>
            </Popover>
        </Stack>
    );
}

const MORISKY_ADHERENCE_LABELS: Record<MoriskyAdherenceLevel, string> = {
    muito_baixa: 'Muito baixa adesão',
    baixa: 'Baixa adesão',
    moderada: 'Adesão moderada',
    alta: 'Alta adesão',
    excelente: 'Excelente adesão'
};

const MORISKY_ITEM_LABELS: Record<string, string> = {
    morisky_1: 'Esquece de usar o CPAP',
    morisky_2: 'Descuidado ao usar o CPAP',
    morisky_3: 'Para quando se sente melhor',
    morisky_4: 'Para quando se sente pior'
};

function MoriskyScoreDisplay({
    score,
    onPopoverOpen,
    popoverOpen,
    popoverAnchorEl,
    onPopoverClose
}: ScoreDisplayProps<MoriskyScoreResult>) {
    const getAdherenceColor = (level: MoriskyAdherenceLevel) => {
        if (level === 'excelente' || level === 'alta') return 'success';
        if (level === 'moderada') return 'info';
        if (level === 'baixa') return 'warning';
        return 'error';
    };

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Detalhes do Resultado">
                <Chip
                    icon={<InfoOutlined fontSize="small" />}
                    label={`Morisky CPAP: ${score.totalScore}/20 - ${MORISKY_ADHERENCE_LABELS[score.adherenceLevel]}`}
                    color={getAdherenceColor(score.adherenceLevel)}
                    size="small"
                    onClick={onPopoverOpen}
                    sx={{
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: 'neutral300.main',
                        backgroundColor: 'neutral100.main',
                        color: 'neutral700.main',
                        "&:hover": {
                            backgroundColor: 'neutral200.main',
                            boxShadow: '1px 1px 16px 1px rgba(0, 0, 0, 0.1)',
                            color: 'neutral700.main',
                            "& .MuiChip-icon": { color: 'neutral700.main' }
                        }
                    }}
                />
            </Tooltip>
            <Popover
                open={popoverOpen}
                anchorEl={popoverAnchorEl}
                onClose={() => onPopoverClose()}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Paper sx={{ p: 2.5, minWidth: 340, maxWidth: 420 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                        Escala de Adesão ao CPAP - Morisky
                    </Typography>

                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', mb: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Pontuação total
                                </Typography>
                                <Typography variant="h4" fontWeight={700} color="primary.main">
                                    {score.totalScore}
                                    <Typography component="span" variant="body1" color="text.secondary" fontWeight={400}>/20</Typography>
                                </Typography>
                            </Box>
                            <Chip
                                label={MORISKY_ADHERENCE_LABELS[score.adherenceLevel]}
                                color={getAdherenceColor(score.adherenceLevel)}
                                size="medium"
                                sx={{ fontWeight: 600 }}
                            />
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {score.adherenceLevel === 'muito_baixa' && 'Uso raro ou praticamente ausente; provável inefetividade terapêutica.'}
                            {score.adherenceLevel === 'baixa' && 'Uso irregular, frequentemente interrompe o tratamento.'}
                            {score.adherenceLevel === 'moderada' && 'Usa com frequência, mas ainda falha em vários dias.'}
                            {score.adherenceLevel === 'alta' && 'Usa corretamente na maioria dos dias.'}
                            {score.adherenceLevel === 'excelente' && 'Uso diário e consistente, provavelmente eficaz.'}
                        </Typography>
                    </Box>

                    <Stack spacing={1}>
                        {Object.entries(score.details).map(([linkId, points]) => (
                            <Box key={linkId} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                                <Typography variant="body2" sx={{ flex: 1, pr: 2 }}>
                                    {MORISKY_ITEM_LABELS[linkId] ?? linkId}
                                </Typography>
                                <Chip label={points} size="small" variant="outlined" sx={{ minWidth: 36, fontWeight: 500 }} />
                            </Box>
                        ))}
                    </Stack>

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
                        4–7: Muito baixa · 8–11: Baixa · 12–14: Moderada · 15–17: Alta · 18–20: Excelente
                    </Typography>
                </Paper>
            </Popover>
        </Stack>
    );
}

function MeqSaScoreDisplay({
    score,
    onPopoverOpen,
    popoverOpen,
    popoverAnchorEl,
    onPopoverClose
}: ScoreDisplayProps<MeqSaScoreResult>) {
    const chipColor: "default" | "error" | "primary" | "secondary" | "info" | "success" | "warning" | undefined =
        score.chronoType === ChronoType.DEFINITELY_MORNING ? 'primary'
            : score.chronoType === ChronoType.MODERATE_MORNING ? 'info'
                : score.chronoType === ChronoType.INTERMEDIATE ? 'success'
                    : score.chronoType === ChronoType.MODERATE_EVENING ? 'warning'
                        : score.chronoType === ChronoType.DEFINITELY_EVENING ? 'error'
                            : 'default';

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Detalhes do Resultado">
                <Chip
                    icon={<InfoOutlined fontSize="small" />}
                    label={`MEQ-SA: ${score.totalScore}/86 - ${score.chronoType}`}
                    color={chipColor}
                    size="small"
                    onClick={onPopoverOpen}
                    sx={{
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: 'neutral300.main',
                        backgroundColor: 'neutral100.main',
                        color: 'neutral700.main',
                        "&:hover": {
                            backgroundColor: 'neutral200.main',
                            boxShadow: '1px 1px 16px 1px rgba(0, 0, 0, 0.1)',
                            color: 'neutral700.main',
                            "& .MuiChip-icon": { color: 'neutral700.main' }
                        }
                    }}
                />
            </Tooltip>
            <Popover
                open={popoverOpen}
                anchorEl={popoverAnchorEl}
                onClose={() => onPopoverClose()}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Paper sx={{ p: 2.5, minWidth: 340, maxWidth: 420 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                        MEQ-SA - Morningness-Eveningness Questionnaire
                    </Typography>

                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', mb: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Pontuação total
                                </Typography>
                                <Typography variant="h4" fontWeight={700} color="primary.main">
                                    {score.totalScore}
                                    <Typography component="span" variant="body1" color="text.secondary" fontWeight={400}>/86</Typography>
                                </Typography>
                            </Box>
                            <Chip
                                label={score.chronoType}
                                color={chipColor}
                                size="medium"
                                sx={{ fontWeight: 600 }}
                            />
                        </Stack>
                    </Box>

                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                        Horários recomendados
                    </Typography>
                    <Stack spacing={1} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2">Início do sono</Typography>
                            <Chip label={score.sleepSchedule.sleepStart} size="small" variant="outlined" sx={{ minWidth: 80, fontWeight: 500 }} />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2">Despertar</Typography>
                            <Chip label={score.sleepSchedule.wakeUp} size="small" variant="outlined" sx={{ minWidth: 80, fontWeight: 500 }} />
                        </Box>
                    </Stack>

                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                        Terapia de luz
                    </Typography>
                    <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'action.hover', mb: 2 }}>
                        <Typography variant="body2">
                            <strong>{score.lightTherapyTime}h</strong> — 30 min, 10.000 lux
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
                        16–30: Vespertino · 31–41: Vespertino moderado · 42–58: Intermediário · 59–69: Matutino moderado · 70–86: Matutino
                    </Typography>
                </Paper>
            </Popover>
        </Stack>
    );
}

const MOCAB_ITEM_LABELS: Record<string, string> = {
    execucao: 'Funções executivas',
    fluencia: 'Fluência verbal',
    orientacao: 'Orientação',
    calculo: 'Cálculo',
    abstracao: 'Abstração',
    evocacao_tardia: 'Evocação tardia',
    percepcao: 'Percepção',
    nomeacao: 'Nomeação',
    atencao_simples: 'Atenção simples',
    atencao_complexa: 'Atenção complexa'
};

function MocabScoreDisplay({
    score,
    onPopoverOpen,
    popoverOpen,
    popoverAnchorEl,
    onPopoverClose
}: ScoreDisplayProps<MocabScoreResult>) {
    const chipLabel = `MoCA-B: ${score.totalScore}/30` + (score.adjustedScore !== score.totalScore ? ` (ajustado ${score.adjustedScore})` : '');
    const chipColor: "default" | "error" | "primary" | "secondary" | "info" | "success" | "warning" | undefined = score.adjustedScore >= 26 ? 'success' : 'warning';
    const hasAdjustments = score.adjustments.schoolingAdjustment > 0 || score.adjustments.literacyAdjustment > 0;

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Detalhes do Resultado">
                <Chip
                    icon={<InfoOutlined fontSize="small" />}
                    label={chipLabel}
                    color={chipColor}
                    size="small"
                    onClick={onPopoverOpen}
                    sx={{
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: 'neutral300.main',
                        backgroundColor: 'neutral100.main',
                        color: 'neutral700.main',
                        "&:hover": {
                            backgroundColor: 'neutral200.main',
                            boxShadow: '1px 1px 16px 1px rgba(0, 0, 0, 0.1)',
                            color: 'neutral700.main',
                            "& .MuiChip-icon": { color: 'neutral700.main' }
                        }
                    }}
                />
            </Tooltip>
            <Popover
                open={popoverOpen}
                anchorEl={popoverAnchorEl}
                onClose={() => onPopoverClose()}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Paper sx={{ p: 2.5, minWidth: 340, maxWidth: 420 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                        MoCA-B - Montreal Cognitive Assessment (Basic)
                    </Typography>

                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', mb: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Pontuação
                                </Typography>
                                <Typography variant="h4" fontWeight={700} color="primary.main">
                                    {score.adjustedScore}
                                    <Typography component="span" variant="body1" color="text.secondary" fontWeight={400}>/30</Typography>
                                </Typography>
                                {hasAdjustments && (
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                        Bruto: {score.totalScore}
                                    </Typography>
                                )}
                            </Box>
                            <Chip
                                label={score.adjustedScore >= 26 ? 'Normal' : 'Alterado'}
                                color={chipColor}
                                size="medium"
                                sx={{ fontWeight: 600 }}
                            />
                        </Stack>
                        {hasAdjustments && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {[
                                    score.adjustments.schoolingAdjustment > 0 && '+1 escolaridade (<4 anos)',
                                    score.adjustments.literacyAdjustment > 0 && '+1 alfabetização (analfabeto)'
                                ].filter(Boolean).join(' · ')}
                            </Typography>
                        )}
                    </Box>

                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                        Pontuação por item
                    </Typography>
                    <Stack spacing={1}>
                        {Object.entries(score.details).map(([linkId, points]) => (
                            <Box key={linkId} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                                <Typography variant="body2" sx={{ flex: 1, pr: 2 }}>
                                    {MOCAB_ITEM_LABELS[linkId] ?? linkId}
                                </Typography>
                                <Chip label={points} size="small" variant="outlined" sx={{ minWidth: 36, fontWeight: 500 }} />
                            </Box>
                        ))}
                    </Stack>

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
                        Máximo 30 pontos. Se total {'<'} 30: +1 se escolaridade {'<'} 4 anos; +1 se analfabeto.
                    </Typography>
                </Paper>
            </Popover>
        </Stack>
    );
}
